import { useState } from "react";

import { Box, Grid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { InputLabel, TextInput } from "components";
import { AppUtils } from "utils";

import CITY_DATA from "../../assets/data/cityData.json";
import POSTAL_DATA from "../../assets/data/postalData.json";

interface PostalAndCityProps<T> {
  form: UseFormReturnType<T>;
  postalFieldName: keyof T | string;
  cityFieldName: keyof T | string;
  disabled?: boolean;
  label?: string;
}

export const PostalAndCity = <T,>({
  form,
  postalFieldName,
  cityFieldName,
  disabled = false,
  label,
}: PostalAndCityProps<T>) => {
  const { t } = useTranslation();
  const [editingField, setEditingField] = useState<string | null>(null);

  const getCityFromPostal = (postalValue: string): string | undefined => {
    if (AppUtils.isEmpty(postalValue) || postalValue.length < 4) {
      return undefined;
    }
    return (POSTAL_DATA as Record<string, string>)[postalValue];
  };

  const getPostalFromCity = (cityValue: string): string | undefined => {
    if (AppUtils.isEmpty(cityValue)) {
      return undefined;
    }
    const cityData = CITY_DATA as Record<string, string>;
    if (cityData[cityValue]) {
      return cityData[cityValue];
    }
    const lowerCityValue = cityValue.toLowerCase();
    const matchingKey = Object.keys(cityData).find((key) => key.toLowerCase() === lowerCityValue);
    return matchingKey ? cityData[matchingKey] : undefined;
  };

  const getFieldKey = (fieldName: string) => {
    return editingField === fieldName ? fieldName : form.key(fieldName);
  };

  const handlePostalChange = (value: string) => {
    form.setFieldValue(postalFieldName, value as any);

    const city = getCityFromPostal(value);
    if (city) {
      form.setFieldValue(cityFieldName, city as any, { forceUpdate: true });
    }
  };

  const handleCityChange = (value: string) => {
    form.setFieldValue(cityFieldName, value as any);

    const postal = getPostalFromCity(value);
    if (postal) {
      form.setFieldValue(postalFieldName, postal as any, { forceUpdate: true });
    }
  };

  return (
    <Box>
      <InputLabel label={label ?? t("user.address.postalAndCity.label")} required={true} />
      <Grid>
        <Grid.Col span={4}>
          <TextInput
            key={getFieldKey(postalFieldName as string)}
            {...form.getInputProps(postalFieldName)}
            maxLength={4}
            required={true}
            onFocus={() => setEditingField(postalFieldName as string)}
            onChange={(event) => handlePostalChange(event.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={8}>
          <TextInput
            key={getFieldKey(cityFieldName as string)}
            {...form.getInputProps(cityFieldName)}
            required={true}
            onFocus={() => setEditingField(cityFieldName as string)}
            onChange={(event) => handleCityChange(event.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
      </Grid>
    </Box>
  );
};
